export type Station = {
  slug: string
  name: string
  streamId: string
  streamUrl: string
  icon: string
  /** Short SEO-focused description */
  summary: string
  /** Longer page copy */
  description: string
  keywords: string[]
}

let liveStations: Station[] | null = null

export function currentStations(): Station[] {
  return liveStations ?? STATIONS
}

export function setLiveStations(next: Station[]) {
  liveStations = next
}

export const STATIONS: Station[] = [
  // Moods — energy / vibe first
  {
    slug: 'upbeat',
    name: 'Upbeat',
    streamId: 'upbeat',
    streamUrl: 'https://live.kcm.fm/02',
    icon: '/station-art/station_upbeat.png',
    summary: 'Energetic Jewish music radio for dancing, driving, and celebrations.',
    description:
      'Upbeat is Music24’s high-energy Jewish music station — lively vocals, simcha favorites, and modern Jewish hits. Stream free online or in the Music24 app.',
    keywords: [
      'upbeat jewish music',
      'simcha music radio',
      'jewish party music',
      'lively jewish radio',
    ],
  },
  {
    slug: 'dance',
    name: 'Dance',
    streamId: 'dance',
    streamUrl: 'https://live.kcm.fm/46',
    icon: '/station-art/station_dance.png',
    summary: 'Dance Jewish music radio for simchas and celebrations.',
    description:
      'Dance is Music24’s high-energy station for wedding floors, parties, and celebrations — continuous Jewish dance music streaming free.',
    keywords: [
      'jewish dance music',
      'simcha dance radio',
      'jewish party music stream',
    ],
  },
  {
    slug: 'vocal',
    name: 'Vocal',
    streamId: 'vocal_upbeat',
    streamUrl: 'https://live.kcm.fm/14',
    icon: '/station-art/station_vocal.png',
    summary: 'Vocal-forward Jewish music radio with standout singers.',
    description:
      'Vocal highlights Jewish music built around great voices — clear vocals, popular arrangements, and continuous live streaming on Music24. During sefira, when people look for sfirah music with the singer in front, this is the station to start with.',
    keywords: [
      'jewish vocal music',
      'jewish singers radio',
      'vocal jewish music stream',
    ],
  },
  {
    slug: 'concerts',
    name: 'Concerts',
    streamId: 'concerts',
    streamUrl: 'https://live.kcm.fm/34',
    icon: '/station-art/station_concerts.png',
    summary: 'Concert-style Jewish music radio — big performances live.',
    description:
      'Concerts brings stage energy and live-performance Jewish music to Music24. Stream free anytime on web, iOS, and Android.',
    keywords: [
      'jewish concert music',
      'jewish live concert radio',
      'concert jewish music stream',
    ],
  },
  {
    slug: 'great-duets',
    name: 'Great Duets',
    streamId: 'great_duets',
    streamUrl: 'https://live.kcm.fm/52',
    icon: '/station-art/station_great_duets.png',
    summary: 'Jewish music duets radio — standout collaborations.',
    description:
      'Great Duets features memorable Jewish music collaborations and vocal pairings. Stream free on Music24 online or in the mobile apps.',
    keywords: [
      'jewish music duets',
      'jewish duet radio',
      'jewish collaboration songs',
    ],
  },
  {
    slug: 'english-classics',
    name: 'English Classics',
    streamId: 'english_classics',
    streamUrl: 'https://live.kcm.fm/22',
    icon: '/station-art/station_english_classics.png',
    summary: 'Classic English-language Jewish music radio.',
    description:
      'English Classics on Music24 streams beloved English Jewish songs and timeless favorites — free live radio online and in the app.',
    keywords: [
      'english jewish music',
      'jewish classics radio',
      'english jewish songs',
    ],
  },
  {
    slug: 'yiddish',
    name: 'Yiddish',
    streamId: 'yiddish',
    streamUrl: 'https://live.kcm.fm/53',
    icon: '/station-art/station_yiddish.png',
    summary: 'Yiddish music radio — classic and contemporary streams.',
    description:
      'Yiddish on Music24 plays Yiddish songs and favorites around the clock. Free Jewish music radio on the web and in the Music24 apps.',
    keywords: [
      'yiddish music radio',
      'yiddish songs stream',
      'jewish yiddish radio',
    ],
  },
  {
    slug: 'kumzitz',
    name: 'Kumzitz',
    streamId: 'kumzitz',
    streamUrl: 'https://live.kcm.fm/19',
    icon: '/station-art/station_kumzitz.png',
    summary: 'Kumzitz-style Jewish music — acoustic, soulful, and warm.',
    description:
      'Kumzitz is Music24’s station for acoustic Jewish music and kumzitz vibes — heartfelt niggunim and campfire energy you can stream anytime.',
    keywords: [
      'kumzitz music',
      'jewish kumzitz radio',
      'niggun radio',
      'acoustic jewish music',
    ],
  },
  {
    slug: 'thank-hashem',
    name: 'Thank Hashem',
    streamId: 'thank_hashem',
    streamUrl: 'https://live.kcm.fm/41',
    icon: '/station-art/station_thank_hashem.png',
    summary: 'Thank Hashem Jewish music radio — gratitude and joy.',
    description:
      'Thank Hashem on Music24 streams grateful, uplifting Jewish music nonstop. Free Jewish music radio on web, iOS, and Android.',
    keywords: [
      'thank hashem radio',
      'thank hashem music',
      'thank hashem stream',
    ],
  },
  {
    slug: 'chazzonos',
    name: 'Chazzonos',
    streamId: 'chazzonos',
    streamUrl: 'https://live.kcm.fm/12',
    icon: '/station-art/station_chazzonos.png',
    summary: 'Live chazzanus and cantorial Jewish music radio.',
    description:
      'Chazzonos brings classic and contemporary chazzanus to Music24 — powerful cantorial voices and nusach-inspired Jewish music, streaming 24/6.',
    keywords: [
      'chazzonos radio',
      'chazzanus',
      'cantorial jewish music',
      'jewish cantor radio',
    ],
  },
  {
    slug: 'relaxing',
    name: 'Relaxing',
    streamId: 'relaxing',
    streamUrl: 'https://live.kcm.fm/10',
    icon: '/station-art/station_relaxing.png',
    summary: 'Calm Jewish music radio for focus, rest, and quiet listening.',
    description:
      'Relaxing plays softer Jewish music — gentle melodies and peaceful tracks for Shabbos prep, studying, or winding down. Listen live on Music24.',
    keywords: [
      'relaxing jewish music',
      'calm jewish radio',
      'soft jewish music stream',
    ],
  },
  // Artists — A–Z
  {
    slug: 'avrum-fried',
    name: 'Avrum Fried',
    streamId: 'avrum_fried',
    streamUrl: 'https://live.kcm.fm/15',
    icon: '/station-art/station_avrum_fried.png',
    summary: 'Avrum Fried Jewish music radio — classic and beloved tracks.',
    description:
      'Tune into Music24’s Avrum Fried station for a continuous stream of Avraham Fried favorites. Free Jewish music radio online and in the app.',
    keywords: [
      'avrum fried radio',
      'avraham fried music',
      'avrum fried stream',
      'jewish music avrum fried',
    ],
  },
  {
    slug: 'benny-friedman',
    name: 'Benny Friedman',
    streamId: 'benny_friedman',
    streamUrl: 'https://live.kcm.fm/63',
    icon: '/station-art/station_benny_friedman.png',
    summary: 'Benny Friedman Jewish music radio station.',
    description:
      'Music24’s Benny Friedman station plays Benny Friedman favorites continuously. Stream free online or download the Music24 app.',
    keywords: [
      'benny friedman radio',
      'benny friedman music',
      'jewish music benny friedman',
    ],
  },
  {
    slug: 'beri-weber',
    name: 'Beri Weber',
    streamId: 'beri_weber',
    streamUrl: 'https://live.kcm.fm/62',
    icon: '/station-art/station_beri_weber.png',
    summary: 'Beri Weber Jewish music radio — stream live.',
    description:
      'Listen to Beri Weber on Music24’s dedicated station — Jewish music streaming free in your browser and on mobile.',
    keywords: [
      'beri weber radio',
      'beri weber music',
      'jewish music beri weber',
    ],
  },
  {
    slug: 'haim-yisrael',
    name: 'Haim Yisrael',
    streamId: 'haim_yisrael',
    streamUrl: 'https://live.kcm.fm/26',
    icon: '/station-art/station_haim_yisrael.png',
    summary: 'Haim Yisrael Jewish music radio — live streaming.',
    description:
      'Stream Haim Yisrael on Music24 for a station built around his Jewish music catalog. Available free on web, iOS, and Android.',
    keywords: [
      'haim yisrael radio',
      'haim yisrael music',
      'jewish music haim yisrael',
    ],
  },
  {
    slug: 'ishay-ribo',
    name: 'Ishay Ribo',
    streamId: 'ishay_ribo',
    streamUrl: 'https://live.kcm.fm/39',
    icon: '/station-art/station_ishay_ribo.png',
    summary: 'Ishay Ribo Jewish music radio — live streaming.',
    description:
      'Tune into Music24’s Ishay Ribo station for a continuous stream of Ishay Ribo favorites. Listen free online or in the app.',
    keywords: [
      'ishay ribo radio',
      'ishay ribo music',
      'ishay ribo stream',
    ],
  },
  {
    slug: 'lipa-schmeltzer',
    name: 'Lipa Schmeltzer',
    streamId: 'lipa_schmeltzer',
    streamUrl: 'https://live.kcm.fm/33',
    icon: '/station-art/station_lipa_schmeltzer.png',
    summary: 'Lipa Schmeltzer Jewish music radio — stream live.',
    description:
      'Listen to Lipa Schmeltzer on Music24’s dedicated station — high-energy Jewish music streaming free on the web and in the mobile apps.',
    keywords: [
      'lipa schmeltzer radio',
      'lipa music stream',
      'jewish music lipa',
    ],
  },
  {
    slug: 'mbd',
    name: 'MBD',
    streamId: 'mbd',
    streamUrl: 'https://live.kcm.fm/16',
    icon: '/station-art/station_mbd.png',
    summary: 'Mordechai Ben David (MBD) Jewish music radio, live.',
    description:
      'The MBD station on Music24 streams Mordechai Ben David classics and Jewish music hits 24/6. Listen free in your browser or on mobile.',
    keywords: [
      'mbd radio',
      'mordechai ben david',
      'mbd music stream',
      'jewish music mbd',
    ],
  },
  {
    slug: 'michoel-schnitzler',
    name: 'Michoel Schnitzler',
    streamId: 'michoel_schnitzler',
    streamUrl: 'https://live.kcm.fm/68',
    icon: '/station-art/station_michoel_schnitzler.png',
    summary: 'Michoel Schnitzler Jewish music radio — stream live.',
    description:
      'Listen to Michoel Schnitzler on Music24’s dedicated station — Jewish music streaming free on the web and in the mobile apps.',
    keywords: [
      'michoel schnitzler radio',
      'michoel schnitzler music',
      'jewish music michoel schnitzler',
    ],
  },
  {
    slug: 'mordechai-shapiro',
    name: 'Mordechai Shapiro',
    streamId: 'mordechai_shapiro',
    streamUrl: 'https://live.kcm.fm/65',
    icon: '/station-art/station_mordechai_shapiro.png',
    summary: 'Mordechai Shapiro Jewish music radio — live.',
    description:
      'Stream Mordechai Shapiro on Music24 for a station built around his Jewish music catalog. Available free on web, iOS, and Android.',
    keywords: [
      'mordechai shapiro radio',
      'mordechai shapiro music',
      'jewish music mordechai shapiro',
    ],
  },
  {
    slug: 'shmueli-ungar',
    name: 'Shmueli Ungar',
    streamId: 'shmueli_ungar',
    streamUrl: 'https://live.kcm.fm/67',
    icon: '/station-art/station_shmueli_ungar.png',
    summary: 'Shmueli Ungar Jewish music radio station.',
    description:
      'The Shmueli Ungar station on Music24 plays Shmueli Ungar favorites nonstop. Stream Jewish music live online or in the app.',
    keywords: [
      'shmueli ungar radio',
      'shmueli ungar music',
      'jewish music shmueli ungar',
    ],
  },
  {
    slug: 'yaakov-shwekey',
    name: 'Yakov Shwekey',
    streamId: 'yaakov_shwekey',
    streamUrl: 'https://live.kcm.fm/30',
    icon: '/station-art/station_yaakov_shwekey.png',
    summary: 'Yaakov Shwekey Jewish music radio station.',
    description:
      'The Yaakov Shwekey station on Music24 plays Shwekey favorites nonstop. Stream Jewish music live online or download the Music24 app.',
    keywords: [
      'yaakov shwekey radio',
      'yakov shwekey music',
      'shwekey stream',
      'jewish music shwekey',
    ],
  },
  {
    slug: 'yossi-green',
    name: 'Yossi Green',
    streamId: 'yossi_green',
    streamUrl: 'https://live.kcm.fm/21',
    icon: '/station-art/station_yossi_green.png',
    summary: 'Yossi Green compositions and Jewish music radio.',
    description:
      'Music24’s Yossi Green station features music written and inspired by Yossi Green — a dedicated Jewish music radio stream you can play anytime.',
    keywords: [
      'yossi green radio',
      'yossi green music',
      'jewish composer radio',
    ],
  },
]

export function getStation(slug: string): Station | undefined {
  return currentStations().find((station) => station.slug === slug)
}

export function stationPath(slug: string): string {
  return `/stations/${slug}`
}

export function stationAbsoluteUrl(slug: string): string {
  return `https://music24.app/stations/${slug}`
}
